import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Mail,
  Pencil,
  Phone,
  Search,
  ShieldCheck,
  UserPlus,
  UsersRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  Card,
  Input,
  Label,
  PageHeader,
  Select,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { initials } from "../lib/utils";
import type {
  CreateManagedUserInput,
  ManagedUser,
  UpdateManagedUserInput,
} from "../types/api";

type UserForm = {
  codigoEmpresa: string;
  usuario: string;
  email: string;
  nome: string;
  telefone: string;
  ativo: boolean;
  admin: boolean;
};

const EMPTY_FORM: UserForm = {
  codigoEmpresa: "52",
  usuario: "",
  email: "",
  nome: "",
  telefone: "",
  ativo: true,
  admin: false,
};

export function UserManagementPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState("0");
  const [activeFilter, setActiveFilter] = useState("true");
  const [draftSearch, setDraftSearch] = useState("");
  const [search, setSearch] = useState("");
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<UserForm>(EMPTY_FORM);

  const companies = useQuery({
    queryKey: ["companies", "user-management"],
    queryFn: api.getCompanies,
  });

  const activeValue =
    activeFilter === "all" ? undefined : activeFilter === "true";
  const users = useQuery({
    queryKey: ["managed-users", companyId, activeFilter, search],
    queryFn: () =>
      api.getManagedUsers(Number(companyId), activeValue, search),
    enabled: Boolean(session?.podeGerenciarUsuarios),
  });

  const counts = useMemo(() => {
    const values = users.data ?? [];
    return {
      total: values.length,
      admins: values.filter((user) => user.admin).length,
      active: values.filter((user) => user.ativo).length,
    };
  }, [users.data]);

  const createUser = useMutation({
    mutationFn: (input: CreateManagedUserInput) =>
      api.createManagedUser(input),
    onSuccess: (result) => {
      toast.success(
        result.emailEnviado
          ? "Usuário criado e convite enviado."
          : "Usuário criado. O convite deverá ser reenviado.",
      );
      closeForm();
      queryClient.invalidateQueries({ queryKey: ["managed-users"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateUser = useMutation({
    mutationFn: ({
      userId,
      input,
    }: {
      userId: number;
      input: UpdateManagedUserInput;
    }) => api.updateManagedUser(userId, input),
    onSuccess: () => {
      toast.success("Usuário atualizado com sucesso.");
      closeForm();
      queryClient.invalidateQueries({ queryKey: ["managed-users"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const resendAccess = useMutation({
    mutationFn: (user: ManagedUser) =>
      api.resendManagedUserAccess(user.codigo),
    onSuccess: (_, user) =>
      toast.success(`Novo link enviado para ${user.email}.`),
    onError: (error: Error) => toast.error(error.message),
  });

  function openCreate() {
    const defaultCompany =
      companyId !== "0"
        ? companyId
        : String(session?.codigoEmpresa ?? 52);
    setEditingUser(null);
    setForm({ ...EMPTY_FORM, codigoEmpresa: defaultCompany });
    setFormOpen(true);
  }

  function openEdit(user: ManagedUser) {
    setEditingUser(user);
    setForm({
      codigoEmpresa: String(user.codigoEmpresa),
      usuario: user.usuario,
      email: user.email,
      nome: user.nome,
      telefone: user.telefone,
      ativo: user.ativo,
      admin: user.admin,
    });
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditingUser(null);
    setForm(EMPTY_FORM);
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    setSearch(draftSearch.trim());
  }

  function submitUser(event: FormEvent) {
    event.preventDefault();
    const common = {
      usuario: form.usuario.trim(),
      email: form.email.trim(),
      nome: form.nome.trim(),
      telefone: form.telefone.trim(),
      admin: form.admin,
    };

    if (editingUser) {
      updateUser.mutate({
        userId: editingUser.codigo,
        input: { ...common, ativo: form.ativo },
      });
      return;
    }

    createUser.mutate({
      ...common,
      codigoEmpresa: Number(form.codigoEmpresa),
    });
  }

  const saving = createUser.isPending || updateUser.isPending;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Administração"
        title="Gestão de usuários"
        description="Cadastre acessos, mantenha os contatos atualizados e controle permissões administrativas."
        actions={
          <Button onClick={openCreate}>
            <UserPlus className="size-4" />
            Novo usuário
          </Button>
        }
      />

      <section className="user-management-stats" aria-label="Resumo de usuários">
        <Card>
          <span className="user-stat-icon"><UsersRound /></span>
          <div><small>Exibidos</small><strong>{counts.total}</strong></div>
        </Card>
        <Card>
          <span className="user-stat-icon user-stat-icon--active"><ShieldCheck /></span>
          <div><small>Ativos</small><strong>{counts.active}</strong></div>
        </Card>
        <Card>
          <span className="user-stat-icon user-stat-icon--admin"><ShieldCheck /></span>
          <div><small>Administradores</small><strong>{counts.admins}</strong></div>
        </Card>
      </section>

      <Card className="user-filter-panel">
        <form onSubmit={submitSearch} className="filter-search">
          <Search />
          <Input
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Nome, usuário, e-mail ou telefone..."
            aria-label="Pesquisar usuários"
          />
          <Button type="submit" variant="secondary" size="sm">
            Pesquisar
          </Button>
        </form>
        <div className="user-filter-controls">
          <div>
            <Label htmlFor="user-company-filter">Empresa</Label>
            <Select
              id="user-company-filter"
              value={companyId}
              onChange={(event) => setCompanyId(event.target.value)}
            >
              <option value="0">Todas as empresas</option>
              {companies.data?.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.empresa}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="user-status-filter">Situação</Label>
            <Select
              id="user-status-filter"
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
            >
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
              <option value="all">Todos</option>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="section-heading">
          <div>
            <h2>Usuários</h2>
            <p>
              {users.isLoading
                ? "Carregando usuários..."
                : `${counts.total} usuário(s) encontrado(s)`}
            </p>
          </div>
          {users.isFetching && !users.isLoading && (
            <span className="refreshing-label">Atualizando...</span>
          )}
        </div>

        {users.isError ? (
          <div className="inline-error">
            Não foi possível consultar os usuários.
            <button onClick={() => users.refetch()}>Tentar novamente</button>
          </div>
        ) : users.data?.length ? (
          <div className="user-table-wrap">
            <table className="user-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Empresa</th>
                  <th>Contato</th>
                  <th>Perfil</th>
                  <th>Situação</th>
                  <th><span className="sr-only">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {users.data.map((user) => (
                  <tr key={user.codigo}>
                    <td>
                      <div className="managed-user-cell">
                        <span>{initials(user.nome || user.usuario)}</span>
                        <div>
                          <strong>{user.nome || user.usuario}</strong>
                          <small>@{user.usuario}</small>
                        </div>
                      </div>
                    </td>
                    <td>{user.empresa}</td>
                    <td>
                      <div className="managed-user-contact">
                        <span><Mail /> {user.email}</span>
                        <span><Phone /> {user.telefone || "Sem telefone"}</span>
                      </div>
                    </td>
                    <td>
                      <span className={user.admin ? "role-badge role-badge--admin" : "role-badge"}>
                        {user.admin ? "Administrador" : "Usuário"}
                      </span>
                    </td>
                    <td>
                      <span className={user.ativo ? "user-status user-status--active" : "user-status"}>
                        <i />
                        {user.ativo ? "Ativo" : "Inativo"}
                      </span>
                    </td>
                    <td>
                      <div className="user-row-actions">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Editar usuário"
                          aria-label={`Editar ${user.usuario}`}
                          onClick={() => openEdit(user)}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Reenviar acesso"
                          aria-label={`Reenviar acesso para ${user.usuario}`}
                          loading={
                            resendAccess.isPending &&
                            resendAccess.variables?.codigo === user.codigo
                          }
                          disabled={!user.ativo}
                          onClick={() => resendAccess.mutate(user)}
                        >
                          <Mail />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state user-empty-state">
            <span><UsersRound /></span>
            <h3>Nenhum usuário encontrado</h3>
            <p>Ajuste os filtros ou cadastre um novo acesso.</p>
          </div>
        )}
      </Card>

      {formOpen && (
        <div className="user-dialog-layer" role="presentation" onMouseDown={closeForm}>
          <section
            className="user-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-dialog-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <span><UserPlus /></span>
              <div>
                <small>{editingUser ? "Editar acesso" : "Novo acesso"}</small>
                <h2 id="user-dialog-title">
                  {editingUser ? editingUser.usuario : "Cadastrar usuário"}
                </h2>
              </div>
              <button type="button" onClick={closeForm} aria-label="Fechar">
                <X />
              </button>
            </header>

            <form onSubmit={submitUser}>
              <div className="user-form-grid">
                <div className="user-form-wide">
                  <Label htmlFor="managed-user-company">Empresa</Label>
                  <div className="input-with-leading-icon">
                    <Building2 />
                    <Select
                      id="managed-user-company"
                      value={form.codigoEmpresa}
                      disabled={Boolean(editingUser)}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          codigoEmpresa: event.target.value,
                        }))
                      }
                      required
                    >
                      <option value="">Selecione a empresa</option>
                      {companies.data?.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.empresa}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="managed-user-name">Nome</Label>
                  <Input
                    id="managed-user-name"
                    value={form.nome}
                    maxLength={200}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, nome: event.target.value }))
                    }
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="managed-user-login">Usuário</Label>
                  <Input
                    id="managed-user-login"
                    value={form.usuario}
                    maxLength={50}
                    autoCapitalize="characters"
                    onChange={(event) =>
                      setForm((current) => ({ ...current, usuario: event.target.value }))
                    }
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="managed-user-email">E-mail</Label>
                  <Input
                    id="managed-user-email"
                    type="email"
                    value={form.email}
                    maxLength={50}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, email: event.target.value }))
                    }
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="managed-user-phone">Telefone</Label>
                  <Input
                    id="managed-user-phone"
                    type="tel"
                    value={form.telefone}
                    maxLength={50}
                    placeholder="(41) 99999-9999"
                    onChange={(event) =>
                      setForm((current) => ({ ...current, telefone: event.target.value }))
                    }
                    required
                  />
                </div>
              </div>

              <div className="user-permission-options">
                <label>
                  <input
                    type="checkbox"
                    checked={form.admin}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, admin: event.target.checked }))
                    }
                  />
                  <span>
                    <ShieldCheck />
                    <strong>Administrador</strong>
                    <small>Pode acessar a gestão de usuários.</small>
                  </span>
                </label>
                {editingUser && (
                  <label>
                    <input
                      type="checkbox"
                      checked={form.ativo}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, ativo: event.target.checked }))
                      }
                    />
                    <span>
                      <UsersRound />
                      <strong>Usuário ativo</strong>
                      <small>Permite autenticação e abertura de chamados.</small>
                    </span>
                  </label>
                )}
              </div>

              {!editingUser && (
                <p className="user-invite-note">
                  O usuário receberá um link seguro para criar a própria senha.
                </p>
              )}

              <footer>
                <Button type="button" variant="secondary" onClick={closeForm}>
                  Cancelar
                </Button>
                <Button type="submit" loading={saving}>
                  {editingUser ? "Salvar alterações" : "Criar e enviar convite"}
                </Button>
              </footer>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
